// Funções puras do módulo de Tarefas — sem JSX, sem side-effects, para
// serem testáveis sem depender de renderização de componente (o frontend
// não tem infraestrutura de teste de componentes configurada).
import moment from "moment";

export const TASK_TYPE_OPTIONS = ["INTERNAL", "CONTACT"];
export const TASK_STATUS_OPTIONS = ["pending", "in_progress", "done", "canceled"];
export const TASK_PRIORITY_OPTIONS = ["low", "medium", "high", "urgent"];

const STATUS_COLORS = {
  pending: { background: "#9e9e9e", color: "#fff" },
  in_progress: { background: "#1976d2", color: "#fff" },
  done: { background: "#2e7d32", color: "#fff" },
  canceled: { background: "#c62828", color: "#fff" }
};

const PRIORITY_COLORS = {
  low: { background: "#78909c", color: "#fff" },
  medium: { background: "#1976d2", color: "#fff" },
  high: { background: "#ef6c00", color: "#fff" },
  urgent: { background: "#c62828", color: "#fff" }
};

const DEFAULT_CHIP_COLOR = { background: "#9e9e9e", color: "#fff" };

export const getStatusColor = status => STATUS_COLORS[status] || DEFAULT_CHIP_COLOR;

export const getPriorityColor = priority => PRIORITY_COLORS[priority] || DEFAULT_CHIP_COLOR;

// --- Controle de acesso (a mesma lógica que o backend aplica — o backend
// continua sendo a autoridade final; isto é só para exibir/ocultar UI) ---

export const canAccessTaskControl = ({ plan, user }) => {
  if (!plan || !user) return false;
  if (plan.useTaskControl !== true) return false;
  return user.profile === "admin" || user.tasksAccessModule === "enabled";
};

export const canCreateTask = ({ user }) => {
  if (!user) return false;
  return user.profile === "admin" || user.tasksCreate === "enabled";
};

// Visão geral: pode ver ALGUMA lista de tarefas (própria ou da empresa)?
// Diferente de canCreateTask — usada para decidir se uma listagem/aba de
// tarefas (ex.: a aba "Tarefas" no ContactDrawer) deve aparecer. O backend
// (ListTasksService) continua sendo quem realmente decide o que cada
// usuário vê — isto é só controle de exibição.
export const canViewTasksList = ({ user }) => {
  if (!user) return false;
  if (user.profile === "admin") return true;
  return user.tasksViewOwn === "enabled" || user.tasksViewAll === "enabled";
};

// --- Validação/normalização de valores ---

const isNonEmptyString = value => typeof value === "string" && value.trim() !== "";

const isPositiveIntegerId = value => {
  if (typeof value !== "number" && typeof value !== "string") return false;
  if (typeof value === "string" && value.trim() === "") return false;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0;
};

// Aceita apenas string de data válida (ex: "2026-08-18", vindo de
// <input type="date">) — rejeita undefined/null/array/objeto/inválida.
export const isValidDateString = value => {
  if (typeof value !== "string" || value.trim() === "") return false;
  const parsed = new Date(value);
  return !Number.isNaN(parsed.getTime());
};

// --- Filtros da listagem ---

// Ao abrir o módulo, limita a agenda ao horizonte operacional de um mês:
// hoje até o mesmo dia do mês seguinte. Recebe uma data de referência para
// manter o cálculo determinístico nos testes (ex.: 31/01 vira 28/02).
export const buildDefaultTaskDueDateRange = referenceDate => {
  const reference = referenceDate === undefined ? moment() : moment(referenceDate);
  const start = reference.isValid() ? reference.startOf("day") : moment().startOf("day");

  return {
    dueDateFrom: start.format("YYYY-MM-DD"),
    dueDateTo: start.clone().add(1, "month").format("YYYY-MM-DD")
  };
};

// Monta os params de GET /tasks explicitamente — nunca envia campo vazio,
// array, objeto ou data inválida. pageNumber é sempre enviado.
export const buildTaskFilterParams = ({
  searchParam,
  status,
  priority,
  type,
  assignedUserId,
  contactId,
  dueDateFrom,
  dueDateTo,
  pageNumber,
  excludeFinished
} = {}) => {
  const params = {
    pageNumber: Number.isInteger(pageNumber) && pageNumber > 0 ? pageNumber : 1
  };

  if (isNonEmptyString(searchParam)) params.searchParam = searchParam.trim();
  if (isNonEmptyString(status) && TASK_STATUS_OPTIONS.includes(status)) {
    params.status = status;
  }
  if (isNonEmptyString(priority) && TASK_PRIORITY_OPTIONS.includes(priority)) {
    params.priority = priority;
  }
  if (isNonEmptyString(type) && TASK_TYPE_OPTIONS.includes(type)) {
    params.type = type;
  }
  if (isPositiveIntegerId(assignedUserId)) params.assignedUserId = assignedUserId;
  if (isPositiveIntegerId(contactId)) params.contactId = contactId;
  if (isValidDateString(dueDateFrom)) params.dueDateFrom = dueDateFrom;
  if (isValidDateString(dueDateTo)) params.dueDateTo = dueDateTo;
  if (excludeFinished === true) params.excludeFinished = true;

  return params;
};

// --- Payload de criação ---

// true quando, para o tipo informado, o campo contato deve ficar
// habilitado/exigido na UI.
export const isContactRequiredForType = type => type === "CONTACT";

// Ao trocar o tipo da tarefa, decide se a seleção de contato atual deve
// ser descartada (sempre que deixar de ser CONTACT).
export const shouldClearContactSelection = newType => newType !== "CONTACT";

// Validação pura do formulário de criação — mesma regra aplicada pelo Yup
// no TaskModal, extraída para ser testável sem renderizar componente e
// reaproveitada como segunda guarda antes do envio (defesa em profundidade).
export const validateTaskFormValues = (values = {}) => {
  const errors = {};

  if (!TASK_TYPE_OPTIONS.includes(values.type)) {
    errors.type = "typeRequired";
  }

  if (!isNonEmptyString(values.title) || values.title.trim().length < 3) {
    errors.title = "titleRequired";
  }

  if (!isPositiveIntegerId(values.assignedUserId)) {
    errors.assignedUserId = "assignedUserRequired";
  }

  if (isContactRequiredForType(values.type) && !isPositiveIntegerId(values.contactId)) {
    errors.contactId = "contactRequired";
  }

  if (values.isRecurring) {
    if (!isValidDateString(values.dueDate)) errors.dueDate = "recurrenceRequiresDueDate";
    const recurrenceCount = Number(values.recurrenceCount);
    if (!Number.isInteger(recurrenceCount) || recurrenceCount < 1 || recurrenceCount > 60) {
      errors.recurrenceCount = "recurrenceCountInvalid";
    }
  }

  return errors;
};

export const isTaskFormValid = values => Object.keys(validateTaskFormValues(values)).length === 0;

// Monta o payload de POST /tasks explicitamente, campo a campo — nunca um
// spread do estado do formulário. companyId/createdByUserId/status NUNCA
// são incluídos (nem sequer lidos de `values`), e campos vazios/não
// aplicáveis são omitidos em vez de enviados como undefined.
export const buildCreateTaskPayload = (values = {}) => {
  const type = values.type;

  const payload = {
    type,
    title: typeof values.title === "string" ? values.title.trim() : values.title,
    priority: isNonEmptyString(values.priority) ? values.priority : "medium",
    assignedUserId: values.assignedUserId
  };

  if (isNonEmptyString(values.description)) {
    payload.description = values.description.trim();
  }

  if (isValidDateString(values.dueDate)) {
    payload.dueDate = values.dueDate;
  }

  if (values.isRecurring === true) {
    payload.isRecurring = true;
    payload.recurrenceCount = Number(values.recurrenceCount);
  }

  // INTERNAL nunca recebe contactId, mesmo que algo tenha ficado
  // selecionado antes da troca de tipo. CONTACT só recebe se de fato
  // houver um contato válido selecionado.
  if (type === "CONTACT" && isPositiveIntegerId(values.contactId)) {
    payload.contactId = values.contactId;
  }

  // ticketId só é enviado para tarefas CONTACT e só quando é um inteiro
  // positivo de verdade — nunca para INTERNAL (mesmo que algo tenha
  // ficado em values de uma seleção anterior), e nunca um UUID (que
  // simplesmente falha isPositiveIntegerId e é descartado aqui, sem
  // nenhuma tentativa de conversão).
  if (type === "CONTACT" && isPositiveIntegerId(values.ticketId)) {
    payload.ticketId = Number(values.ticketId);
  }

  return payload;
};

// --- Deduplicação de páginas (scroll infinito) ---

// Mescla uma nova página de tarefas na lista existente, substituindo
// entradas repetidas pelo id em vez de duplicá-las.
export const mergeTasksById = (existingTasks = [], newTasks = []) => {
  const merged = [...existingTasks];

  newTasks.forEach(task => {
    const index = merged.findIndex(t => t.id === task.id);
    if (index !== -1) {
      merged[index] = task;
    } else {
      merged.push(task);
    }
  });

  return merged;
};

// --- Identificação de dono e permissões de ação (Etapa 7B) ---
//
// Mesma definição de posse do backend (TaskPermissionGuard.ts:
// assignedUserId === user.id || createdByUserId === user.id). O backend
// continua sendo a autoridade final — isto é só controle de exibição.

// Normaliza um id vindo de qualquer lugar (JWT decodifica user.id como
// string; a Task vem com ids numéricos) para um inteiro positivo, ou
// `null` se não representar um id válido. Rejeita null/undefined, string
// vazia, array, objeto e NaN.
export const normalizeId = value => {
  if (value === null || value === undefined) return null;
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) return null;
  return parsed;
};

const isTaskAdminUser = user => user?.profile === "admin";

export const isTaskOwner = (task, user) => {
  const userId = normalizeId(user?.id);
  if (userId === null) return false;
  return normalizeId(task?.assignedUserId) === userId || normalizeId(task?.createdByUserId) === userId;
};

const OPEN_TASK_STATUSES = ["pending", "in_progress"];

// Cada função abaixo: admin passa pela checagem de permissão específica,
// mas SEMPRE respeita o status permitido (um admin não pode "iniciar" uma
// tarefa já concluída, por exemplo). Usuário comum precisa do módulo, da
// flag específica da ação E de ser dono.
export const canEditTask = ({ task, user }) => {
  if (!task || !user || !OPEN_TASK_STATUSES.includes(task.status)) return false;
  if (isTaskAdminUser(user)) return true;
  if (user.tasksAccessModule !== "enabled" || user.tasksEdit !== "enabled") return false;
  return isTaskOwner(task, user);
};

export const canStartTask = ({ task, user }) => {
  if (!task || !user || task.status !== "pending") return false;
  if (isTaskAdminUser(user)) return true;
  if (user.tasksAccessModule !== "enabled" || user.tasksStart !== "enabled") return false;
  return isTaskOwner(task, user);
};

export const canCompleteTask = ({ task, user }) => {
  if (!task || !user || task.status !== "in_progress") return false;
  if (isTaskAdminUser(user)) return true;
  if (user.tasksAccessModule !== "enabled" || user.tasksComplete !== "enabled") return false;
  return isTaskOwner(task, user);
};

export const canTransferTask = ({ task, user }) => {
  if (!task || !user || !OPEN_TASK_STATUSES.includes(task.status)) return false;
  if (isTaskAdminUser(user)) return true;
  if (user.tasksAccessModule !== "enabled" || user.tasksTransfer !== "enabled") return false;
  return isTaskOwner(task, user);
};

export const canCancelTask = ({ task, user }) => {
  if (!task || !user || !OPEN_TASK_STATUSES.includes(task.status)) return false;
  if (isTaskAdminUser(user)) return true;
  if (user.tasksAccessModule !== "enabled" || user.tasksCancel !== "enabled") return false;
  return isTaskOwner(task, user);
};

// --- "Avisar contato" (Etapa 12) ---
//
// Mesmo critério do backend (NotifyTaskContactService.ts: checagem inline +
// guardContactSendable) replicado aqui só para controle de EXIBIÇÃO — o
// backend continua sendo a autoridade real a cada chamada. Grupos nunca são
// bloqueados por esta checagem (mesma regra do guardContactSendable: o
// `number` de um grupo é o jid do grupo, não um identificador de pessoa).
const onlyDigits = value => String(value || "").replace(/\D/g, "");

export const isContactPhoneTrustedForNotify = contact => {
  if (!contact) return false;
  if (contact.isGroup) return true;
  if (contact.phoneUnconfirmed) return false;

  const rawNumber = String(contact.number || "");
  if (!onlyDigits(rawNumber)) return false;
  if (rawNumber.toLowerCase().includes("@lid")) return false;

  const numberDigits = onlyDigits(rawNumber);
  const lidDigits = onlyDigits(contact.lid);
  if (numberDigits && lidDigits && numberDigits === lidDigits) return false;

  return true;
};

// Pode ver o botão "Avisar contato"? Admin ou tasksComplete+dono (mesma
// flag reaproveitada pelo backend para esta ação — não existe uma 9ª flag
// dedicada). Não checa plano nem readOnly aqui: nenhum irmão (canCompleteTask
// etc.) checa plano, e readOnly já é garantido por quem só renderiza
// TaskActionsBar quando !readOnly (TaskDetailsModal/index.js).
export const canNotifyTaskContact = ({ task, user }) => {
  if (!task || !user) return false;
  if (task.status !== "done") return false;
  if (task.type !== "CONTACT") return false;
  if (!task.contact) return false;
  if (task.contactNotifiedAt) return false;
  if (!isContactPhoneTrustedForNotify(task.contact)) return false;
  if (isTaskAdminUser(user)) return true;
  if (user.tasksAccessModule !== "enabled" || user.tasksComplete !== "enabled") return false;
  return isTaskOwner(task, user);
};

// Nome confiável para a saudação da mensagem sugerida: rejeita ausente,
// somente-números, igual ao telefone, igual ao LID, ou contendo "@lid" —
// nunca deixa o código do LID (que pode ficar temporariamente em
// contact.name antes da resolução) vazar para a mensagem enviada ao
// próprio contato.
export const isTrustedContactName = (name, { number, lid } = {}) => {
  if (typeof name !== "string") return false;
  const trimmed = name.trim();
  if (trimmed === "") return false;
  if (/^\d+$/.test(trimmed)) return false;
  if (/@lid/i.test(trimmed)) return false;

  const nameDigits = onlyDigits(trimmed);
  // Também rejeita telefones/identificadores apenas formatados com
  // pontuação, espaços, parênteses, sinal de + ou hífens.
  if (nameDigits && !/[\p{L}]/u.test(trimmed)) return false;
  if (nameDigits && number && nameDigits === onlyDigits(number)) return false;
  if (nameDigits && lid && nameDigits === onlyDigits(lid)) return false;

  return true;
};

// Monta a mensagem sugerida inicial (editável) do modal "Avisar contato".
// `translate` é injetado pelo chamador (i18n.t) em vez de importado aqui —
// mantém este helper puro e testável com um stub simples, sem depender do
// estado global de idioma da interface. Nunca insere undefined/null/id —
// título vazio é omitido (nunca uma linha "undefined"); sem nome confiável,
// a saudação inteira vira neutra (nunca insere o texto de fallback de nome
// dentro de "Olá, ...!"). Quebras de linha preservadas via "\n".
export const buildNotifyContactSuggestedMessage = ({ contact, taskTitle, translate }) => {
  const t = typeof translate === "function" ? translate : key => key;

  const trustedName = isTrustedContactName(contact?.name, {
    number: contact?.number,
    lid: contact?.lid
  })
    ? contact.name.trim()
    : null;

  const greeting = trustedName
    ? t("tasks.details.actions.notifyContact.suggested.greeting", { name: trustedName })
    : t("tasks.details.actions.notifyContact.suggested.greetingNeutral");

  const title = typeof taskTitle === "string" && taskTitle.trim() !== "" ? taskTitle.trim() : "";

  return [
    greeting,
    t("tasks.details.actions.notifyContact.suggested.intro"),
    title,
    t("tasks.details.actions.notifyContact.suggested.completed"),
    t("tasks.details.actions.notifyContact.suggested.closing")
  ]
    .filter(line => line !== "")
    .join("\n");
};

// Validação pura da mensagem — trim só acontece aqui (na hora de
// validar/enviar), nunca no campo enquanto o usuário digita.
export const validateNotifyContactMessage = value => {
  if (typeof value !== "string") return "required";
  const trimmed = value.trim();
  if (trimmed === "") return "required";
  if (trimmed.length < 3) return "tooShort";
  if (trimmed.length > 4096) return "tooLong";
  return null;
};

// Payload de POST /tasks/:taskId/notify-contact — contém SOMENTE `message`,
// já trimada.
export const buildNotifyTaskContactPayload = (values = {}) => ({
  message: typeof values.message === "string" ? values.message.trim() : values.message
});

// Classifica o erro de "Avisar contato" para decidir a mensagem/ação da UI,
// sem depender de toastError genérico (cujo texto padrão de rede/timeout
// incentiva "tente novamente" — o oposto do que esta ação específica exige).
// Puro: só lê o formato de um objeto de erro estilo axios, nunca importa
// api/i18n/estado global.
export const classifyNotifyContactError = err => {
  const code = err?.response?.data?.error || err?.response?.data?.message;

  if (code === "ERR_TASK_CONTACT_ALREADY_NOTIFIED") return "alreadyNotified";
  if (code === "ERR_CONTACT_PHONE_UNCONFIRMED") return "phoneUnconfirmed";
  if (code === "ERR_TASK_NOTIFY_SEND_FAILED" || code === "ERR_TASK_NOTIFY_REGISTRATION_FAILED") {
    return "ambiguous";
  }
  // Sem resposta nenhuma (rede caiu/CORS/reset) — depois de um envio já
  // disparado ao provedor, isso também é ambíguo: pode ter sido entregue
  // sem a resposta ter chegado.
  if (!err?.response) return "ambiguous";
  if (err?.code === "ECONNABORTED" || /timeout/i.test(String(err?.message || ""))) return "ambiguous";

  return "generic";
};

// --- Datas seguras (nunca "Invalid date", nunca deslocam o dia por fuso) ---

// Formata para exibição; retorna null (nunca "Invalid date") quando
// ausente/ inválida — o componente decide o texto de fallback traduzido.
export const formatTaskDate = (value, format = "DD/MM/YYYY") => {
  if (!value) return null;
  if (typeof value !== "string" && !(value instanceof Date)) return null;
  const parsed =
    value instanceof Date ? moment(value) : moment(value, moment.ISO_8601, true);
  return parsed.isValid() ? parsed.format(format) : null;
};

// Converte para o valor esperado por <input type="date"> (YYYY-MM-DD).
// Extrai a data diretamente da string (sem passar por Date()/moment com
// fuso local) para nunca deslocar o dia — uma data ISO "2026-08-18T00:00:00Z"
// não pode virar 17/08 num fuso negativo.
export const toDateInputValue = value => {
  if (!value) return "";
  if (typeof value === "string") {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const year = parsed.getUTCFullYear();
  const month = String(parsed.getUTCMonth() + 1).padStart(2, "0");
  const day = String(parsed.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// --- Payload de edição (PUT /tasks/:taskId) ---
//
// Sempre monta exatamente estes 4 campos, explicitamente — nunca status,
// assignedUserId, contactId, ticketId, type, companyId, createdByUserId ou
// campos de conclusão/cancelamento (nem sequer são lidos de `values`).
// Limpar o prazo ou a descrição envia `null` (sinaliza "limpar" para o
// backend); campo nunca fica `undefined` dentro do payload.
export const buildUpdateTaskPayload = (values = {}) => ({
  title: typeof values.title === "string" ? values.title.trim() : values.title,
  description: isNonEmptyString(values.description) ? values.description.trim() : null,
  priority: isNonEmptyString(values.priority) ? values.priority : "medium",
  dueDate: isValidDateString(values.dueDate) ? values.dueDate : null
});

// --- Payload das ações de ciclo de vida ---

// completionDescription é opcional — omitido por completo quando vazio,
// nunca enviado como string vazia.
export const buildCompleteTaskPayload = (values = {}) => {
  const trimmed =
    typeof values.completionDescription === "string" ? values.completionDescription.trim() : "";
  const payload = {};
  if (trimmed) payload.completionDescription = trimmed;
  return payload;
};

export const validateCompletionDescription = value => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value !== "string") return "invalid";
  if (value.trim().length > 5000) return "tooLong";
  return null;
};

export const buildTransferTaskPayload = (values = {}) => ({
  newAssignedUserId: normalizeId(values.newAssignedUserId)
});

export const buildCancelTaskPayload = (values = {}) => ({
  cancelReason: typeof values.cancelReason === "string" ? values.cancelReason.trim() : values.cancelReason
});

export const validateCancelReason = value => {
  if (typeof value !== "string") return "required";
  const trimmed = value.trim();
  if (trimmed.length === 0) return "required";
  if (trimmed.length < 3) return "tooShort";
  if (trimmed.length > 255) return "tooLong";
  return null;
};

// --- Merge de páginas de histórico/comentários ---
//
// Reaproveita a deduplicação por id de mergeTasksById, mas SEMPRE reordena
// o resultado inteiro depois — nunca só concatena. Isso é necessário porque
// páginas podem chegar fora de ordem (ex.: uma página intermediária
// carregada depois de um comentário novo ter sido inserido localmente).

const sortByCreatedAtThenId = (a, b, direction) => {
  const timeA = new Date(a.createdAt).getTime();
  const timeB = new Date(b.createdAt).getTime();

  if (timeA !== timeB) {
    return direction === "asc" ? timeA - timeB : timeB - timeA;
  }

  // Desempate determinístico por id quando createdAt é idêntico.
  const idA = Number(a.id);
  const idB = Number(b.id);
  return direction === "asc" ? idA - idB : idB - idA;
};

// Comentários: cronológico crescente (mais antigo primeiro), igual ao
// backend — nunca usar mergeTasksById puro aqui sem reordenar depois.
export const mergeCommentsChronologically = (existingComments = [], newComments = []) => {
  const merged = mergeTasksById(existingComments, newComments);
  return [...merged].sort((a, b) => sortByCreatedAtThenId(a, b, "asc"));
};

// Histórico: mais recente primeiro, igual ao backend.
export const mergeHistoryNewestFirst = (existingHistory = [], newHistory = []) => {
  const merged = mergeTasksById(existingHistory, newHistory);
  return [...merged].sort((a, b) => sortByCreatedAtThenId(a, b, "desc"));
};

// --- Estratégia de atualização da listagem após uma ação (Etapa 7B) ---
//
// Só o backend reproduz corretamente a busca em título/descrição/contato/
// responsável/ticket — com busca ou qualquer filtro ativo, a tarefa
// alterada exige uma nova consulta (página 1) em vez de tentar replicar
// essa lógica de filtro aqui no frontend.
export const hasActiveTaskFilters = (searchParam, filters = {}) =>
  Boolean(
    (typeof searchParam === "string" && searchParam.trim()) ||
      filters.status ||
      filters.priority ||
      filters.type ||
      filters.assignedUserId ||
      filters.contactId ||
      filters.dueDateFrom ||
      filters.dueDateTo
  );
